import { useState, type FormEvent } from 'react';
import { Check, Clock, Copy, Link2, UserPlus, UsersRound } from 'lucide-react';
import type { AccountDto, AccountLinkDto, ProjectDto } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import { Table, type Column } from '../../shared/ui/Table.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { ApiError } from '../../shared/api/client.js';
import {
  useAccountActions,
  useAccounts,
  usePlatformSettings,
  useProjects,
  useUpdatePlatformSettings,
} from '../../shared/api/queries.js';
import { useSession } from '../../shared/auth/session.js';
import { RolePermissionsCard } from './RolePermissionsCard.js';
import './accounts.css';

type Role = AccountDto['role'];
type Status = AccountDto['status'];

const ROLE_LABELS: Readonly<Record<Role, string>> = {
  platform_access: 'Accès plateforme',
  central_access: 'Accès central',
};

const STATUS: Readonly<
  Record<Status, { label: string; tone: 'warning' | 'positive' | 'negative' | 'neutral' }>
> = {
  pending_activation: { label: "En attente d'activation", tone: 'warning' },
  active: { label: 'Actif', tone: 'positive' },
  suspended: { label: 'Suspendu', tone: 'negative' },
  removed: { label: 'Retiré', tone: 'neutral' },
};

const MIN_IDLE_MINUTES = 5;
const MAX_IDLE_MINUTES = 480;

interface HandedLink {
  readonly name: string;
  readonly link: AccountLinkDto;
}

/**
 * "Comptes" (module M14, central access): create an account with its profile
 * and perimeter, change the perimeter, suspend, reinstate, remove, issue a new
 * link; and the inactivity duration of every session (functional §9.2–9.5).
 */
export function AccountsPage() {
  const { account: me } = useSession();
  const { data: accounts = [], isLoading } = useAccounts();
  const { data: projects = [] } = useProjects();
  const actions = useAccountActions();
  const [creating, setCreating] = useState(false);
  const [editingScope, setEditingScope] = useState<AccountDto | null>(null);
  const [handed, setHanded] = useState<HandedLink | null>(null);
  const [error, setError] = useState<string | null>(null);

  const projectName = (id: string) => projects.find((project) => project.id === id)?.name ?? 'Projet inconnu';

  const run = async (work: () => Promise<unknown>) => {
    setError(null);
    try {
      await work();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  const columns: Array<Column<AccountDto>> = [
    {
      key: 'account',
      header: 'Compte',
      render: (row) => (
        <div className="accounts__who">
          <span className="accounts__name">
            {row.displayName}
            {row.id === me?.id && <span className="accounts__me">vous</span>}
          </span>
          <span className="accounts__email">{row.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Profil',
      render: (row) => (
        <Badge tone={row.role === 'central_access' ? 'brand' : 'neutral'}>{ROLE_LABELS[row.role]}</Badge>
      ),
    },
    {
      key: 'scope',
      header: 'Périmètre',
      render: (row) =>
        row.role === 'central_access' ? (
          <span className="accounts__muted">Tous les projets</span>
        ) : (
          <span>{row.projectScope.map(projectName).join(', ')}</span>
        ),
    },
    {
      key: 'status',
      header: 'Statut',
      render: (row) => <Badge tone={STATUS[row.status].tone}>{STATUS[row.status].label}</Badge>,
    },
    {
      key: 'created',
      header: 'Créé le',
      render: (row) => new Date(row.createdAt).toLocaleDateString('fr-FR'),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (row) => (
        <AccountActions
          account={row}
          isMe={row.id === me?.id}
          onScope={() => setEditingScope(row)}
          onSuspend={(suspended) =>
            void run(() => actions.setSuspension.mutateAsync({ id: row.id, suspended }))
          }
          onLink={() =>
            void run(async () =>
              setHanded({ name: row.displayName, link: await actions.issueLink.mutateAsync(row.id) }),
            )
          }
          onRemove={() => {
            if (
              window.confirm(
                `Retirer le compte de ${row.displayName} ? Ses sessions sont fermées et il ne pourra plus se connecter. Ce retrait est définitif.`,
              )
            ) {
              void run(() => actions.remove.mutateAsync(row.id));
            }
          }}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Comptes"
        description="Aucune inscription libre : chaque compte est créé ici, avec son profil et son périmètre. Un accès plateforme ne voit jamais les données d'un autre projet."
        actions={
          <Button variant="primary" icon={<UserPlus />} onClick={() => setCreating(true)} disabled={creating}>
            Nouveau compte
          </Button>
        }
      />

      {error && (
        <p className="accounts__error" role="alert">
          {error}
        </p>
      )}

      {handed && <HandOverCard handed={handed} onClose={() => setHanded(null)} />}

      {creating && (
        <CreateAccountCard
          projects={projects}
          onCancel={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false);
            setHanded(created);
          }}
        />
      )}

      {editingScope && (
        <ScopeEditorCard
          key={editingScope.id}
          account={editingScope}
          projects={projects}
          onCancel={() => setEditingScope(null)}
          onSave={(projectScope) =>
            void run(async () => {
              await actions.changeScope.mutateAsync({ id: editingScope.id, projectScope });
              setEditingScope(null);
            })
          }
        />
      )}

      <Card title="Comptes de la plateforme" icon={<UsersRound size={15} />} padded={false}>
        <Table
          columns={columns}
          rows={accounts}
          rowKey={(row) => row.id}
          empty={isLoading ? 'Chargement…' : 'Aucun compte.'}
        />
      </Card>

      <RolePermissionsCard />

      <SessionSettingsCard />
    </>
  );
}

interface AccountActionsProps {
  readonly account: AccountDto;
  readonly isMe: boolean;
  readonly onScope: () => void;
  readonly onSuspend: (suspended: boolean) => void;
  readonly onLink: () => void;
  readonly onRemove: () => void;
}

function AccountActions({ account, isMe, onScope, onSuspend, onLink, onRemove }: AccountActionsProps) {
  if (account.status === 'removed') return null;
  return (
    <div className="accounts__actions">
      {account.role === 'platform_access' && (
        <Button variant="ghost" onClick={onScope}>
          Périmètre
        </Button>
      )}
      {(account.status === 'pending_activation' || account.status === 'active') && (
        <Button variant="ghost" onClick={onLink}>
          {account.status === 'pending_activation' ? "Lien d'activation" : 'Lien de réinitialisation'}
        </Button>
      )}
      {!isMe && account.status === 'active' && (
        <Button variant="ghost" onClick={() => onSuspend(true)}>
          Suspendre
        </Button>
      )}
      {!isMe && account.status === 'suspended' && (
        <Button variant="ghost" onClick={() => onSuspend(false)}>
          Réactiver
        </Button>
      )}
      {!isMe && (
        <Button variant="danger" onClick={onRemove}>
          Retirer
        </Button>
      )}
    </div>
  );
}

interface CreateAccountCardProps {
  readonly projects: readonly ProjectDto[];
  readonly onCancel: () => void;
  readonly onCreated: (handed: HandedLink) => void;
}

function CreateAccountCard({ projects, onCancel, onCreated }: CreateAccountCardProps) {
  const actions = useAccountActions();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('platform_access');
  const [scope, setScope] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (role === 'platform_access' && scope.length === 0) {
      setError('Un accès plateforme couvre au moins un projet.');
      return;
    }
    setError(null);
    try {
      const created = await actions.create.mutateAsync({
        displayName,
        email,
        role,
        projectScope: role === 'platform_access' ? scope : [],
      });
      onCreated({ name: created.account.displayName, link: created.link });
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  return (
    <Card title="Nouveau compte" icon={<UserPlus size={15} />} className="accounts__panel">
      <form className="accounts__form" onSubmit={submit}>
        <label className="accounts__field">
          <span>Nom</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            maxLength={120}
          />
        </label>
        <label className="accounts__field">
          <span>Adresse e-mail</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <fieldset className="accounts__fieldset">
          <legend>Profil</legend>
          {(Object.keys(ROLE_LABELS) as Role[]).map((value) => (
            <label key={value} className="accounts__choice">
              <input type="radio" name="role" checked={role === value} onChange={() => setRole(value)} />
              <span>
                <strong>{ROLE_LABELS[value]}</strong>
                <small>
                  {value === 'platform_access'
                    ? 'Indicateurs, parcours, recommandations et guidances des projets de son périmètre.'
                    : 'Tout, y compris les données brutes, les projets, les comptes et le script.'}
                </small>
              </span>
            </label>
          ))}
        </fieldset>
        {role === 'platform_access' && (
          <ProjectPicker projects={projects} selected={scope} onChange={setScope} />
        )}
        {error && (
          <p className="accounts__error" role="alert">
            {error}
          </p>
        )}
        <div className="accounts__buttons">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={actions.create.isPending}>
            {actions.create.isPending ? 'Création…' : 'Créer le compte'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface ScopeEditorCardProps {
  readonly account: AccountDto;
  readonly projects: readonly ProjectDto[];
  readonly onCancel: () => void;
  readonly onSave: (projectScope: string[]) => void;
}

function ScopeEditorCard({ account, projects, onCancel, onSave }: ScopeEditorCardProps) {
  const [scope, setScope] = useState<string[]>([...account.projectScope]);
  return (
    <Card title={`Périmètre de ${account.displayName}`} className="accounts__panel">
      <p className="accounts__lead">
        Le changement s'applique dès sa prochaine action, sans nouvelle connexion.
      </p>
      <ProjectPicker projects={projects} selected={scope} onChange={setScope} />
      <div className="accounts__buttons">
        <Button variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
        <Button variant="primary" onClick={() => onSave(scope)} disabled={scope.length === 0}>
          Enregistrer le périmètre
        </Button>
      </div>
    </Card>
  );
}

interface ProjectPickerProps {
  readonly projects: readonly ProjectDto[];
  readonly selected: readonly string[];
  readonly onChange: (selected: string[]) => void;
}

function ProjectPicker({ projects, selected, onChange }: ProjectPickerProps) {
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);
  return (
    <fieldset className="accounts__fieldset">
      <legend>Projets du périmètre</legend>
      {projects.length === 0 && <p className="accounts__muted">Aucun projet.</p>}
      {projects.map((project) => (
        <label key={project.id} className="accounts__check">
          <input
            type="checkbox"
            checked={selected.includes(project.id)}
            onChange={() => toggle(project.id)}
          />
          <span>{project.name}</span>
        </label>
      ))}
    </fieldset>
  );
}

/** The link no e-mail carried: shown once, to hand over (phase 7 note, decision 1). */
function HandOverCard({ handed, onClose }: { handed: HandedLink; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const { link } = handed;
  const until = new Date(link.expiresAt).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
  const what = link.purpose === 'activation' ? "Lien d'activation" : 'Lien de réinitialisation';

  return (
    <Card
      title={`${what} de ${handed.name}`}
      icon={<Link2 size={15} />}
      className="accounts__panel accounts__handover"
      actions={
        <Button variant="ghost" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      {link.url ? (
        <>
          <p className="accounts__lead">
            Transmettez ce lien à la personne par un canal sûr. Il ne sera plus affiché ; il sert une seule
            fois et reste valable jusqu'au {until}.
          </p>
          <div className="accounts__link">
            <code data-testid="account-link">{link.url}</code>
            <Button
              icon={copied ? <Check /> : <Copy />}
              onClick={() => {
                void navigator.clipboard.writeText(link.url ?? '');
                setCopied(true);
              }}
            >
              {copied ? 'Copié' : 'Copier'}
            </Button>
          </div>
        </>
      ) : (
        <p className="accounts__lead">Le lien a été envoyé par e-mail ; il reste valable jusqu'au {until}.</p>
      )}
    </Card>
  );
}

function SessionSettingsCard() {
  const { data } = usePlatformSettings();
  const update = useUpdatePlatformSettings();
  const [draft, setDraft] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const value = draft ?? String(data?.sessionIdleMinutes ?? '');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      await update.mutateAsync({ sessionIdleMinutes: Number(value) });
      setDraft(null);
      setMessage(
        'Enregistré. La nouvelle durée vaut pour toutes les sessions dès leur prochain renouvellement.',
      );
    } catch (cause) {
      setMessage(errorMessage(cause));
    }
  };

  return (
    <Card title="Sessions" icon={<Clock size={15} />} className="accounts__settings">
      <form className="accounts__idle" onSubmit={submit}>
        <label className="accounts__field accounts__field--inline">
          <span>Fermer une session après</span>
          <input
            type="number"
            min={MIN_IDLE_MINUTES}
            max={MAX_IDLE_MINUTES}
            step={1}
            value={value}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Durée d'inactivité en minutes"
          />
          <span>minutes sans action</span>
        </label>
        <Button type="submit" variant="secondary" disabled={update.isPending || draft === null}>
          Enregistrer
        </Button>
      </form>
      <p className="accounts__muted">
        Entre {MIN_IDLE_MINUTES} et {MAX_IDLE_MINUTES} minutes. Une session dure de toute façon 8 heures au
        plus.
      </p>
      {message && <p className="accounts__lead">{message}</p>}
    </Card>
  );
}

const ERROR_MESSAGES: Readonly<Record<string, string>> = {
  EMAIL_ALREADY_USED: 'Un compte utilise déjà cette adresse e-mail.',
  INVALID_ACCOUNT: "Vérifiez l'adresse e-mail, le nom et le périmètre.",
  INVALID_ACCOUNT_TRANSITION: "Cette action n'est pas possible dans l'état actuel du compte.",
  INVALID_SESSION_SETTINGS: `La durée doit être un nombre entier de minutes, entre ${MIN_IDLE_MINUTES} et ${MAX_IDLE_MINUTES}.`,
  PROJECT_NOT_FOUND: "Un des projets choisis n'existe plus.",
  VALIDATION_FAILED: 'Vérifiez les champs saisis.',
};

function errorMessage(cause: unknown): string {
  return (cause instanceof ApiError && ERROR_MESSAGES[cause.code]) || "L'opération a échoué. Réessayez.";
}
